import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { SocialShare } from "@/components/SocialShare";
import { Calendar, Clock, User } from "lucide-react";

const ReturningToWalden = () => {
  const navigate = useNavigate();
  
  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <>
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              onClick={handleGoBack}
              className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
            
            <div className="mb-8">
              <Badge className="mb-4">Philosophy</Badge>
              <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
                Returning to Walden — Why Thoreau's Woods Still Matter
              </h1>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <time dateTime="2025-10-04">October 4, 2025</time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>8 min read</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>Piotr</span>
                </div>
              </div>
              
              <SocialShare 
                url={window.location.href}
                title="Returning to Walden — Why Thoreau's Woods Still Matter"
                description="Henry David Thoreau's return to simplicity still speaks to us today. In our digital wilderness, his woods offer a path home."
              />
            </div>
            
            <div className="relative overflow-hidden rounded-2xl mb-12">
              <img
                src="/lovable-uploads/walden-cabin.jpg"
                alt="A solitary cabin by a misty lake surrounded by autumn trees and mountains, reminiscent of Thoreau's Walden Pond"
                className="w-full h-[400px] md:h-[500px] object-cover grayscale"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="px-6 pb-24">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                "I went to the woods because I wished to live deliberately,
                to front only the essential facts of life,
                and see if I could not learn what it had to teach,
                and not, when I came to die, discover that I had not lived."
                <footer className="text-sm text-muted-foreground mt-2 not-italic">
                  — Henry David Thoreau, <em>Walden; or, Life in the Woods</em>, Boston: Ticknor and Fields, 1854, p. 67
                </footer>
              </blockquote>

              <div className="space-y-8 text-lg leading-relaxed text-foreground">
                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">A Nineteenth-Century Revolution That Still Speaks to Us</h2>
                
                <p className="mb-6 leading-relaxed">
                  In 1845, Henry David Thoreau left behind the noise of Concord, Massachusetts, and built a small cabin by Walden Pond.
                  He stayed there for two years, two months, and two days — to live simply, to think freely, and to learn what it meant to be fully alive.
                </p>
                
                <p className="mb-6 leading-relaxed">
                  Nearly two centuries later, his decision feels more radical than ever.
                  We may no longer chop wood or fetch water from a pond, but our lives are equally cluttered — not by objects, but by notifications, demands, and digital noise.
                </p>
                
                <p className="mb-6 leading-relaxed">
                  Where Thoreau sought solitude from industrial progress, we seek refuge from information overload.
                  His "woods" were literal; ours are metaphorical — a digital wilderness that consumes our attention and fragments our sense of presence.
                </p>
                
                <p className="mb-6 leading-relaxed">And just like Thoreau, we too long to reclaim what is real.</p>

                <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Simplicity as a Form of Freedom</h2>
                
                <p className="mb-6 leading-relaxed">
                  Thoreau's philosophy wasn't anti-society.
                  It was about alignment — the courage to step aside and question what we call "necessity."
                  He wrote:
                </p>
                
                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                  "The cost of a thing is the amount of what I will call life which is required to be exchanged for it."
                  <footer className="text-sm text-muted-foreground mt-2 not-italic">
                    <em>Walden</em>, p. 45
                  </footer>
                </blockquote>
                
                <p className="mb-6 leading-relaxed">
                  That reflection hits differently today.
                  We might not trade hours for firewood, but we constantly trade our time, attention, and peace for convenience and connection that rarely satisfy.
                  Thoreau reminds us that freedom lies in choosing less — not as deprivation, but as clarity.
                </p>

                <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Nature as a Mirror</h2>
                
                <p className="mb-6 leading-relaxed">
                  Walden was more than shelter for Thoreau — it was a teacher. By living close to the land, he began to notice patterns: the changing seasons, the rhythm of a day, the cycles of growth and rest. And in those patterns, he recognized a template for living.
                </p>
                
                <p className="mb-6 leading-relaxed">
                  Today, we've largely disconnected from those cycles. We work under artificial light, eat out of season, and live in climates controlled by machines. We've optimized convenience — but lost something essential.
                </p>
                
                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                  "In wildness is the preservation of the world."
                  <footer className="text-sm text-muted-foreground mt-2 not-italic">
                    — Henry David Thoreau, <em>Walking</em>, 1862
                  </footer>
                </blockquote>
                
                <p className="mb-6 leading-relaxed">
                  Thoreau knew that wildness wasn't just physical. It was a state of mind — the freedom to be, to wonder, to wander without agenda.
                </p>

                <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Returning to Our Own Woods</h2>
                
                <p className="mb-6 leading-relaxed">
                  Most of us won't build a cabin by a pond. But we can cultivate Walden within — a mental and emotional space that honors stillness, simplicity, and presence.
                </p>
                
                <p className="mb-6 leading-relaxed">At Mystic Forest, this is precisely what we create: places where people can return to themselves, away from the noise, back to what's real.</p>
                
                <p className="mb-6 leading-relaxed">Not an escape, but a return.</p>

                <p className="text-xl font-light italic text-accent mb-6">
                  "I learned this, at least, by my experiment: that if one advances confidently in the direction of his dreams, and endeavors to live the life which he has imagined, he will meet with a success unexpected in common hours."
                </p>
                
                <p className="text-right text-sm text-muted-foreground mb-6">— Thoreau, <em>Walden</em></p>

                <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                  <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                  <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                    <li>Thoreau, Henry David. <em>Walden; or, Life in the Woods</em>. Boston: Ticknor and Fields, 1854.</li>
                    <li>Thoreau, Henry David. <em>Walking</em>. Atlantic Monthly, June 1862.</li>
                    <li>Emerson, Ralph Waldo. <em>Nature</em>. Boston: James Munroe and Company, 1836.</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default ReturningToWalden;
